import { createFileRoute, Link } from "@tanstack/react-router";
import { Code } from "../../components/Code";

export const Route = createFileRoute("/docs/api-reference")({
  component: ApiReference,
});

function ApiReference() {
  return (
    <>
      <h1>API reference</h1>
      <p className="lead">
        Four endpoints. Your backend touches one of them; the script handles the rest.
      </p>

      <h2>Browser API</h2>
      <h3>notify.subscribe(userId)</h3>
      <p>
        Asks permission, creates the push subscription, and registers it against{" "}
        <span className="icode">userId</span>. Returns the subscription. Safe to call repeatedly.
      </p>
      <Code fname="app.js" badge="frontend" copyText={"await notify.subscribe(currentUser.id)"}>
{`await notify.subscribe(currentUser.id)`}
      </Code>

      <h2>HTTP API</h2>

      <h3>Create a project</h3>
      <div className="endpoint">
        <span className="method">POST</span> /v1/tokens
      </div>
      <p>
        Mints a project with a publishable + secret key pair. This is what{" "}
        <span className="icode">npx notify-dev init</span> calls. Rate-limited per IP.
      </p>
      <Code badge="response" copyText={'{ "publishableKey": "ntfy_pk_...", "secretKey": "ntfy_sk_..." }'}>
{`{
  "publishableKey": "ntfy_pk_...",   // browser, /subscribe
  "secretKey": "ntfy_sk_..."         // server, /send (keep private)
}`}
      </Code>

      <h3>Get the VAPID public key</h3>
      <div className="endpoint">
        <span className="method">GET</span> /config
      </div>
      <p>Returns the public key the browser needs to subscribe. notify.js calls this for you.</p>
      <Code badge="response" copyText={'{ "vapidPublicKey": "BPx..." }'}>
{`{ "vapidPublicKey": "BPx..." }`}
      </Code>

      <h3>Register a subscription</h3>
      <div className="endpoint">
        <span className="method">POST</span> /subscribe
      </div>
      <p>
        Called by notify.js. Authenticates with the <strong>publishable</strong> key (the{" "}
        <span className="icode">token</span> field). Stores the browser subscription against a user id.
      </p>
      <Code
        badge="request"
        copyText={`{\n  "token": "ntfy_pk_...",\n  "userId": "user_123",\n  "subscription": { "endpoint": "...", "keys": { "p256dh": "...", "auth": "..." } }\n}`}
      >
{`{
  "token": "ntfy_pk_...",          // publishable key
  "userId": "user_123",
  "subscription": {
    "endpoint": "https://...",
    "keys": { "p256dh": "...", "auth": "..." }
  }
}`}
      </Code>

      <h3>Send a notification</h3>
      <div className="endpoint">
        <span className="method">POST</span> /send
      </div>
      <p>
        The only endpoint your backend calls. Authenticates with the <strong>secret</strong> key (the{" "}
        <span className="icode">token</span> field) — keep it server-side. Looks up the user’s device
        and delivers the push. Rate-limited per key and per IP.
      </p>
      <Code
        badge="request"
        copyText={`{\n  "token": "ntfy_sk_...",\n  "userId": "user_123",\n  "title": "Acme",\n  "body": "Your export is ready!"\n}`}
      >
{`{
  "token": "ntfy_sk_...",          // secret key (server-side only)
  "userId": "user_123",
  "title": "Acme",                 // optional, defaults to "notify"
  "body": "Your export is ready!"
}`}
      </Code>

      <h2>Status codes</h2>
      <ul>
        <li>
          <span className="icode">200</span> — delivered / stored
        </li>
        <li>
          <span className="icode">400</span> — malformed body
        </li>
        <li>
          <span className="icode">401</span> — unknown or wrong-type key (e.g. a publishable key on{" "}
          <span className="icode">/send</span>)
        </li>
        <li>
          <span className="icode">404</span> — no subscription for that user
        </li>
        <li>
          <span className="icode">429</span> — rate limit exceeded
        </li>
      </ul>

      <div className="doc-foot">
        <Link to="/docs/how-it-works" className="ilink">
          ← How it works
        </Link>
        <Link to="/docs" className="ilink">
          Back to overview →
        </Link>
      </div>
    </>
  );
}
